import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parseServerDate } from '../utils/formatDate';

export default function Settings({ showToast }) {
  const navigate = useNavigate();
  const { authFetch, user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({ name:'', company:'', phone:'' });
  const [pwForm, setPwForm] = useState({ currentPassword:'', newPassword:'', confirm:'' });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name||'', company: user.company||'', phone: user.phone||'' });
  }, [user]);

  const saveProfile = async e => {
    e.preventDefault(); setSaving(true);
    const res = await authFetch('/api/auth/profile', { method: 'PUT', body: JSON.stringify(profile) });
    if (res.ok) { showToast('Profile saved'); refreshUser?.(); }
    else showToast('Save failed', 'error');
    setSaving(false);
  };

  const changePassword = async e => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) { showToast('Passwords do not match', 'error'); return; }
    if (pwForm.newPassword.length < 6) { showToast('Password must be at least 6 characters', 'error'); return; }
    setSavingPw(true);
    const res = await authFetch('/api/auth/change-password', { method: 'POST', body: JSON.stringify(pwForm) });
    const data = await res.json();
    if (res.ok) { showToast('Password changed'); setPwForm({ currentPassword:'', newPassword:'', confirm:'' }); }
    else showToast(data.error || 'Password change failed', 'error');
    setSavingPw(false);
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Settings</div>
        <div className="page-sub">Account profile, password, and preferences</div>
      </div>

      <div className="g2" style={{ alignItems: 'start' }}>
        {/* Profile */}
        <div>
          <div className="sec-header"><div className="sec-title">Profile</div><div className="sec-line"></div></div>
          <div className="card" style={{ marginBottom: 20 }}>
            <form onSubmit={saveProfile}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Company / Organization</label>
                <input className="form-input" value={profile.company} onChange={e => setProfile({ ...profile, company: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" type="tel" value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} placeholder="+91 98765 43210" />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" value={user?.email || ''} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }} />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Email cannot be changed after registration</div>
              </div>
              <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Profile'}</button>
            </form>
          </div>

          {/* Account info */}
          <div className="sec-header"><div className="sec-title">Account</div><div className="sec-line"></div></div>
          <div className="card">
            {[
              { label: 'Plan', value: user?.plan?.toUpperCase(), color: user?.plan === 'pro' ? 'var(--accent)' : user?.plan === 'enterprise' ? 'var(--accent2)' : 'var(--text2)' },
              { label: 'AI Credits', value: user?.credits + ' remaining', color: 'var(--gold)' },
              { label: 'Member Since', value: user?.created_at ? parseServerDate(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—', color: 'var(--text2)' },
              { label: 'Role', value: user?.role?.toUpperCase(), color: user?.role === 'admin' ? 'var(--accent2)' : 'var(--text2)' },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{row.label}</span>
                <span style={{ fontFamily: 'var(--ff-m)', fontSize: 12, fontWeight: 600, color: row.color }}>{row.value}</span>
              </div>
            ))}
            <div style={{ paddingTop: 14 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/billing')}>
                {user?.plan === 'free' ? '⬆️ Upgrade to Pro' : '💳 Manage Billing'} →
              </button>
            </div>
          </div>
        </div>

        {/* Security */}
        <div>
          <div className="sec-header"><div className="sec-title">Security</div><div className="sec-line"></div></div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title">Change Password</div>
            <form onSubmit={changePassword}>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <div style={{position:'relative'}}>
                <input className="form-input" type={showCurrent?'text':'password'}
                  value={pwForm.currentPassword} onChange={e=>setPwForm({...pwForm,currentPassword:e.target.value})} required style={{paddingRight:40}}/>
                <span onClick={()=>setShowCurrent(p=>!p)} style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',cursor:'pointer',fontSize:16,color:'var(--text-3)',userSelect:'none'}}>
                  {showCurrent?'🙈':'👁'}
                </span>
              </div>
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <div style={{position:'relative'}}>
                <input className="form-input" type={showNew?'text':'password'}
                  value={pwForm.newPassword} onChange={e=>setPwForm({...pwForm,newPassword:e.target.value})} required minLength={6} style={{paddingRight:40}}/>
                <span onClick={()=>setShowNew(p=>!p)} style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',cursor:'pointer',fontSize:16,color:'var(--text-3)',userSelect:'none'}}>
                  {showNew?'🙈':'👁'}
                </span>
              </div>
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <div style={{position:'relative'}}>
                <input className="form-input" type={showConfirm?'text':'password'}
                  value={pwForm.confirm} onChange={e=>setPwForm({...pwForm,confirm:e.target.value})} required style={{paddingRight:40}}/>
                <span onClick={()=>setShowConfirm(p=>!p)} style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',cursor:'pointer',fontSize:16,color:'var(--text-3)',userSelect:'none'}}>
                  {showConfirm?'🙈':'👁'}
                </span>
              </div>
              </div>
              <button className="btn btn-primary" disabled={savingPw}>{savingPw ? 'Changing...' : 'Change Password'}</button>
            </form>
          </div>

          <div className="sec-header"><div className="sec-title">About</div><div className="sec-line"></div></div>
          <div className="card">
            <div className="card-title">Enpower Org DNA™</div>
            {[
              { label: 'Assessment Coverage', value: '120 questions across 12 dimensions' },
              { label: 'Scoring Engine', value: 'Pattern-detection + AI-powered insights' },
              { label: 'Security', value: 'Encrypted at rest · Bank-grade TLS' },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{row.label}</span>
                <span style={{ fontSize: 11.5, color: 'var(--text3)', textAlign: 'right', maxWidth: '60%' }}>{row.value}</span>
              </div>
            ))}
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text3)' }}>
              Built by Enpower Process Consultants Pvt Ltd · Confidential
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
